import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { MapOutlined, Search } from '@mui/icons-material';
import { loadGoogleMaps } from '../../utils/googleMapsLoader';
import { boundsOf, jitterPins } from './mapPins';
import { fmtPriceShort } from './format';
import { brand, status as statusColors, text } from '../../theme/colors';

const ZIMBABWE_CENTER = { lat: -19.0154, lng: 29.1549 };
const MIN_ZOOM_ON_FIT = 6;
const MAX_ZOOM_ON_FIT = 15;
// The public pins are rounded to the 0.01° grid, so five decimals is already far
// more precision than the data carries — it only keeps the URL readable.
const BBOX_DECIMALS = 5;

/**
 * A rounded price tag drawn as an SVG data URI — cheaper than one OverlayView
 * per listing. Icons are cached by label and highlight state: building the same
 * data URI for every marker on every hover is the expensive part.
 */
const iconCache = new Map();
const pinIcon = (maps, label, highlighted) => {
  const key = `${label}|${highlighted}`;
  const hit = iconCache.get(key);
  if (hit) return hit;
  const width = Math.max(44, 9 * label.length + 18);
  const bg = highlighted ? brand.primaryDark : brand.primary;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="34">
    <rect x="1" y="1" rx="12" ry="12" width="${width - 2}" height="24" fill="${bg}" stroke="#ffffff" stroke-width="2"/>
    <polygon points="${width / 2 - 5},24 ${width / 2 + 5},24 ${width / 2},33" fill="${bg}"/>
    <text x="${width / 2}" y="18" font-family="Plus Jakarta Sans, Arial, sans-serif" font-size="12"
      font-weight="700" fill="#ffffff" text-anchor="middle">${label}</text>
  </svg>`;
  const icon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    anchor: new maps.Point(width / 2, 34),
  };
  iconCache.set(key, icon);
  return icon;
};

/**
 * The right-hand map on the browse page: one price pin per listing, hover synced
 * with the cards, and a "Search this area" button after the buyer pans.
 *
 * The map object is created once and never rebuilt — markers are diffed by
 * listing id — because re-creating it on every result set would throw away the
 * buyer's zoom and pan, which is the whole point of searching by area.
 */
export default function ListingsMap({
  items = [], hoveredId = null, onHover, onSelect, onSearchArea, height = '100%',
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const markersRef = useRef(new Map());
  const mapListenersRef = useRef([]);
  const fittedRef = useRef(false);
  const prevHoveredRef = useRef(null);
  const aliveRef = useRef(true);
  // A fitBounds or setZoom we issued ourselves also fires dragend/zoom_changed.
  // Without this flag the buyer is offered "Search this area" for a viewport
  // they never touched.
  const programmaticRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [showSearchArea, setShowSearchArea] = useState(false);
  // realtor.com-style map controls: road map or satellite, and "search as I
  // move the map" — when on, a pan or zoom re-runs the search by itself.
  const [mapType, setMapType] = useState('roadmap');
  const [autoSearch, setAutoSearch] = useState(false);
  const autoSearchRef = useRef(false);
  autoSearchRef.current = autoSearch && !!onSearchArea;
  const searchAreaRef = useRef(null);
  // Handlers live in a ref so the marker listeners, bound once per marker,
  // always see the current callbacks without being torn down on every render.
  const cbRef = useRef({});
  cbRef.current = { onHover, onSelect };

  useEffect(() => {
    aliveRef.current = true;
    loadGoogleMaps()
      .then((maps) => {
        if (!aliveRef.current || !elRef.current || mapRef.current) return;
        mapsRef.current = maps;
        mapRef.current = new maps.Map(elRef.current, {
          center: ZIMBABWE_CENTER,
          zoom: 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        const moved = () => {
          if (programmaticRef.current || !aliveRef.current) return;
          if (autoSearchRef.current) {
            // Wait for the map to settle, then search what the buyer is looking at.
            maps.event.addListenerOnce(mapRef.current, 'idle', () => {
              if (aliveRef.current && searchAreaRef.current) searchAreaRef.current();
            });
            return;
          }
          setShowSearchArea(true);
        };
        mapListenersRef.current = [
          mapRef.current.addListener('dragend', moved),
          mapRef.current.addListener('zoom_changed', moved),
        ];
        setReady(true);
      })
      .catch(() => { if (aliveRef.current) setUnavailable(true); });

    const markers = markersRef.current;
    return () => {
      aliveRef.current = false;
      mapListenersRef.current.forEach((l) => l && l.remove && l.remove());
      mapListenersRef.current = [];
      markers.forEach((m) => {
        if (m.eqListeners) m.eqListeners.forEach((l) => l && l.remove && l.remove());
        m.setMap(null);
      });
      markers.clear();
      mapRef.current = null;
      mapsRef.current = null;
    };
  }, []);

  /** Suppress the "Search this area" prompt for a viewport change we caused. */
  const withoutPrompt = useCallback((fn) => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    programmaticRef.current = true;
    fn();
    if (maps?.event?.addListenerOnce && map) {
      maps.event.addListenerOnce(map, 'idle', () => { programmaticRef.current = false; });
    } else {
      programmaticRef.current = false;
    }
  }, []);

  // Diff markers against the current result set. `ready` is a dependency, not
  // just a guard: listings usually resolve before the Maps script does, so
  // without it this effect runs once against no map and never runs again.
  useEffect(() => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!ready || !maps || !map) return;

    const pins = jitterPins(items.map((l) => ({
      id: l.id, lat: l.location?.pin?.lat, lng: l.location?.pin?.lng, price: l.price_usd,
    })));
    const wanted = new Set(pins.map((p) => p.id));

    markersRef.current.forEach((marker, id) => {
      if (wanted.has(id)) return;
      if (marker.eqListeners) marker.eqListeners.forEach((l) => l && l.remove && l.remove());
      marker.setMap(null);
      markersRef.current.delete(id);
    });

    pins.forEach((p) => {
      const label = fmtPriceShort(p.price);
      const existing = markersRef.current.get(p.id);
      if (existing) {
        existing.setPosition({ lat: p.lat, lng: p.lng });
        return;
      }
      const marker = new maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map,
        icon: pinIcon(maps, label, false),
        title: label,
      });
      marker.eqLabel = label;
      marker.eqListeners = [
        marker.addListener('mouseover', () => cbRef.current.onHover && cbRef.current.onHover(p.id)),
        marker.addListener('mouseout', () => cbRef.current.onHover && cbRef.current.onHover(null)),
        marker.addListener('click', () => cbRef.current.onSelect && cbRef.current.onSelect(p.id)),
      ];
      markersRef.current.set(p.id, marker);
    });

    // Frame the first result set only: after that the buyer owns the viewport.
    if (!fittedRef.current && pins.length > 0) {
      const b = boundsOf(pins);
      if (b) {
        withoutPrompt(() => {
          if (b.south === b.north && b.west === b.east) {
            map.setCenter({ lat: b.south, lng: b.west });
            map.setZoom(14);
          } else {
            map.fitBounds(new maps.LatLngBounds(
              new maps.LatLng(b.south, b.west), new maps.LatLng(b.north, b.east),
            ), 48);
          }
          const z = map.getZoom && map.getZoom();
          if (z != null && z < MIN_ZOOM_ON_FIT) map.setZoom(MIN_ZOOM_ON_FIT);
          if (z != null && z > MAX_ZOOM_ON_FIT) map.setZoom(MAX_ZOOM_ON_FIT);
        });
      }
      fittedRef.current = true;
      setShowSearchArea(false);
    }
  }, [items, ready, withoutPrompt]);

  // Hover highlight: repaint only the marker being entered and the one being
  // left, so a mouse travelling down the card list does not redraw the whole map.
  useEffect(() => {
    const maps = mapsRef.current;
    if (!ready || !maps) return;
    const paint = (id, on) => {
      const marker = id && markersRef.current.get(id);
      if (!marker) return;
      marker.setIcon(pinIcon(maps, marker.eqLabel, on));
      marker.setZIndex(on ? 999 : 1);
    };
    if (prevHoveredRef.current !== hoveredId) paint(prevHoveredRef.current, false);
    paint(hoveredId, true);
    prevHoveredRef.current = hoveredId;
  }, [hoveredId, ready]);

  const searchArea = useCallback(() => {
    const map = mapRef.current;
    if (!map || !onSearchArea) return;
    const b = map.getBounds && map.getBounds();
    if (!b) return;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    setShowSearchArea(false);
    onSearchArea([sw.lat(), sw.lng(), ne.lat(), ne.lng()].map((n) => Number(n).toFixed(BBOX_DECIMALS)));
  }, [onSearchArea]);
  searchAreaRef.current = searchArea;

  useEffect(() => {
    if (ready && mapRef.current && mapRef.current.setMapTypeId) mapRef.current.setMapTypeId(mapType);
  }, [ready, mapType]);

  if (unavailable) {
    return (
      <Box
        sx={{
          height,
          minHeight: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: statusColors.neutralBg,
          borderRadius: 2,
        }}
      >
        <MapOutlined sx={{ fontSize: 40, color: text.tertiary }} />
        <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 600 }}>Map unavailable</Typography>
        <Typography variant="caption" sx={{ color: text.tertiary }}>Browse the list on the left instead.</Typography>
      </Box>
    );
  }

  return (
    <Box
      role="region"
      aria-label="Map of verified properties"
      sx={{ position: 'relative', height, minHeight: 240, borderRadius: 2, overflow: 'hidden' }}
    >
      {/* A map is unusable to a screen reader, so say what it holds and where
          the same information can be read instead. */}
      <Box
        sx={{
          position: 'absolute', width: 1, height: 1, p: 0, m: '-1px',
          overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {`Map of ${items.length} verified ${items.length === 1 ? 'property' : 'properties'}; the same properties are listed alongside.`}
      </Box>
      <Box ref={elRef} sx={{ width: '100%', height: '100%' }} />
      <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pointerEvents: 'none' }}>
        <ToggleButtonGroup
          exclusive size="small" value={mapType} onChange={(_, v) => v && setMapType(v)} aria-label="Map style"
          sx={{ pointerEvents: 'auto', bgcolor: '#fff', borderRadius: '12px', p: '4px', boxShadow: '0 2px 8px rgba(15,23,42,0.16)',
            '& .MuiToggleButton-root': { border: 0, borderRadius: '9px !important', px: 1.75, textTransform: 'none', fontWeight: 600, color: '#1e293b' },
            '& .Mui-selected': { bgcolor: '#1e293b !important', color: '#fff !important' } }}
        >
          <ToggleButton value="roadmap">Map</ToggleButton>
          <ToggleButton value="hybrid">Satellite</ToggleButton>
        </ToggleButtonGroup>
        {onSearchArea && (
          <FormControlLabel
            sx={{ pointerEvents: 'auto', ml: 'auto', mr: 0, bgcolor: '#fff', borderRadius: '12px', pl: 0.5, pr: 1.75, height: 44,
              boxShadow: '0 2px 8px rgba(15,23,42,0.16)', '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 600 } }}
            control={<Checkbox size="small" checked={autoSearch} onChange={(e) => { setAutoSearch(e.target.checked); setShowSearchArea(false); }} />}
            label="Search as I move the map"
          />
        )}
      </Box>
      {showSearchArea && onSearchArea && (
        <Button
          size="small"
          variant="contained"
          startIcon={<Search />}
          onClick={searchArea}
          sx={{
            position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)',
            textTransform: 'none', fontWeight: 700, borderRadius: 999,
            bgcolor: '#fff', color: brand.primary, '&:hover': { bgcolor: '#fff' },
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          }}
        >
          Search this area
        </Button>
      )}
    </Box>
  );
}
